<script setup lang="ts">
import i18n from '@/lang/i18n'
import { Ref, reactive, ref, computed, onMounted } from 'vue'
import {
  saveDataToSessionStorage,
  loadDataFromSessionStorage,
  clearSessionStorage
} from '@/services/functions/sessionStorage'
import SingleOperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete.vue'
import getOperationOffice from '@/types/responses/getOperationOffice'

const SESSION_STORAGE_NAME = 'gpsTrackerSettingSearchForm'

type SearchForm = {
  operationOffice: getOperationOffice | null
}

const hide: Ref<boolean> = ref(false)
const form = reactive<SearchForm>({
  operationOffice: null,
})

const emits = defineEmits<{
  (e: 'search', form: SearchForm): void
  (e: 'formchange'): void
}>()
const props = defineProps<{
  isLoading: boolean
}>()

onMounted(()=> {
  const savedFormValues = loadDataFromSessionStorage(SESSION_STORAGE_NAME) as SearchForm | null
  if (savedFormValues) {
    form.operationOffice = savedFormValues.operationOffice;
  }
})

const title = computed(() => {
  return hide.value ? "" : i18n.global.t('pages.common.filter.title')
})
const searchAreaClass = computed(() => {
  return hide.value ? "search-area-hide p-4" : "search-area p-4"
})
const changeSearchButton = computed(() => {
  return props.isLoading ? i18n.global.t('pages.common.button.loading') : i18n.global.t('pages.common.button.exec')
})
function toggleSidebar () {
  hide.value = !hide.value
}

function handleClickSearch() {
  emits('search', form)
  saveDataToSessionStorage(SESSION_STORAGE_NAME, form)
}
function clearForm () {
  form.operationOffice = null
  clearSessionStorage(SESSION_STORAGE_NAME);
}

</script>
<template>
  <div :class="searchAreaClass">
    <h4 class="d-flex justify-content-between mt-0">
      {{ title }}
      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fit=""
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        sandboxuid="0"
        @click="toggleSidebar"
      >
        <template v-if="hide">
          <path d="M7.615 11.795l4.59-4.59-1.41-1.41-6 6 6 6 1.41-1.41-4.59-4.59zm9.59 6v-12h-2v12h2z" fill-rule="evenodd" sandboxuid="0"></path>
        </template>
        <template v-if="!hide">
          <path d="M6 7.41L10.438 12 6 16.59 7.363 18l5.802-6-5.802-6L6 7.41zM16.066 6H18v12h-1.934V6z" sandboxuid="0" />
        </template>
      </svg>
    </h4>
    <div :class="`strpied-tabled-with-hover ${hide ? 'd-none' : '' }`">
      <div class="d-grid gap-2">
        <SingleOperationOfficeAutoComplete
          v-model="form.operationOffice"
          :clearable="true"
          selectFirstIfOnlyOne
        />
      </div>

      <v-divider
        :thickness="3"
        class="my-2"
      />

      <div class="d-flex search-buttons" >
        <v-btn
          class="ma-2 exec-btn"
          @click="handleClickSearch"
          :disabled="props.isLoading"
          :style="{
            backgroundColor : props.isLoading ? '#B3B8BB' : ''
          }"
        >
          {{ changeSearchButton }}
        </v-btn>
        <v-btn
          class="ma-2 reset-btn"
          :disabled="props.isLoading"
          @click="clearForm"
        >
          {{ i18n.global.t('pages.common.button.reset') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>
